// Leaderboard calculation logic
// Supports two ranking methods:
// 1. By Category: Players ranked by total placement points across all categories
// 2. By Day: Players ranked by daily placement points, calculated from category performance each day

// Calculate effective points for a player in a game
export const calculateEffectivePoints = (result, allResultsForGame) => {
  if (!result) return 0;

  // If player participated, return their entered points
  if (result.participated && result.pointsEntered !== null) {
    return result.pointsEntered;
  }

  // If player did not participate, calculate average from participants
  const participantResults = allResultsForGame.filter(
    r => r.participated && r.pointsEntered !== null
  );

  if (participantResults.length === 0) {
    return 0; // No average available yet
  }

  const sum = participantResults.reduce((acc, r) => acc + r.pointsEntered, 0);
  return sum / participantResults.length;
};

// Calculate category scores for all players (placement points based on actual game performance)
export const calculateCategoryScores = (players, games, results, categoryId) => {
  const categoryGames = games.filter(
    g => g.categoryId === categoryId && g.status === 'played'
  );

  const numPlayers = players.length;

  // First, calculate sum of actual points for each player
  const actualScores = players.map(player => {
    let totalActualPoints = 0;

    categoryGames.forEach(game => {
      const gameResults = results.filter(r => r.gameId === game.id);
      const playerResult = gameResults.find(r => r.playerId === player.id);
      const effectivePoints = calculateEffectivePoints(playerResult, gameResults);
      totalActualPoints += effectivePoints;
    });

    return {
      playerId: player.id,
      playerName: player.displayName,
      actualPoints: totalActualPoints,
      gamesPlayed: categoryGames.length
    };
  });

  // Sort by actual points to determine rankings
  const sorted = [...actualScores].sort((a, b) => b.actualPoints - a.actualPoints);

  // First pass: assign ranks and group tied players
  let currentRank = 1;
  let previousActualPoints = null;
  const rankedPlayers = [];

  sorted.forEach((player) => {
    if (previousActualPoints !== null && player.actualPoints < previousActualPoints) {
      currentRank = rankedPlayers.length + 1;
    }

    previousActualPoints = player.actualPoints;

    rankedPlayers.push({
      playerId: player.playerId,
      playerName: player.playerName,
      actualScore: player.actualPoints,
      gamesPlayed: player.gamesPlayed,
      rank: currentRank
    });
  });

  // Second pass: calculate averaged placement points for tied players
  return rankedPlayers.map((player, index) => {
    // Find all players with the same rank
    const playersWithSameRank = rankedPlayers.filter(p => p.rank === player.rank);

    // Calculate average placement points for tied positions
    // If tied at rank 1 with 2 players: (numPlayers + (numPlayers-1)) / 2
    // If tied at rank 2 with 3 players: ((numPlayers-1) + (numPlayers-2) + (numPlayers-3)) / 3
    let sumOfPositionPoints = 0;
    for (let i = 0; i < playersWithSameRank.length; i++) {
      const positionRank = player.rank + i;
      const positionPoints = numPlayers - positionRank + 1;
      sumOfPositionPoints += positionPoints;
    }
    const placementPoints = sumOfPositionPoints / playersWithSameRank.length;

    return {
      playerId: player.playerId,
      playerName: player.playerName,
      score: placementPoints, // Averaged placement points for tied players
      actualScore: player.actualScore,
      gamesPlayed: player.gamesPlayed,
      rank: player.rank
    };
  });
};

// Calculate overall scores for all players (sum of placement points from all categories)
// This is the "By Category" ranking method
export const calculateOverallScoresByCategory = (players, games, results, categories) => {
  const activeCategories = categories.filter(c => c.status === 'active');

  return players.map(player => {
    let totalPlacementPoints = 0;
    const categoryBreakdown = {};

    activeCategories.forEach(category => {
      // Get category leaderboard which returns placement points
      const categoryScores = calculateCategoryScores(players, games, results, category.id);

      // Find player's placement points and actual score in this category
      const playerCategoryData = categoryScores.find(p => p.playerId === player.id);
      const placementPoints = playerCategoryData ? playerCategoryData.score : 0;
      const actualScore = playerCategoryData ? playerCategoryData.actualScore : 0;

      categoryBreakdown[category.id] = {
        placementPoints,
        actualScore
      };
      totalPlacementPoints += placementPoints;
    });

    return {
      playerId: player.id,
      playerName: player.displayName,
      score: totalPlacementPoints,
      categoryBreakdown
    };
  });
};

// Legacy alias for backwards compatibility
export const calculateOverallScores = calculateOverallScoresByCategory;

// Calculate overall scores using "By Day" ranking method
// This is a 2-stage calculation:
// Stage 1: For each day, for each category with games, rank players and award category-day placement points
// Stage 2: For each day, sum category-day placement points to get daily scores, rank, and award daily placement points
// Overall: Sum of daily placement points across all days
export const calculateOverallScoresByDay = (players, games, results, categories) => {
  const activeCategories = categories.filter(c => c.status === 'active');
  const playedGames = games.filter(g => g.status === 'played');

  if (playedGames.length === 0) {
    return players.map(player => ({
      playerId: player.id,
      playerName: player.displayName,
      score: 0,
      dailyBreakdown: {}
    }));
  }

  // Get unique dates from played games
  const dates = [...new Set(playedGames.map(g => g.date))].sort();
  const numPlayers = players.length;

  // Store daily placement points for each player
  const playerDailyPoints = {};
  const dailyBreakdown = {};

  players.forEach(player => {
    playerDailyPoints[player.id] = {};
    dailyBreakdown[player.id] = {};
  });

  // Process each day
  dates.forEach(date => {
    const gamesOnDate = playedGames.filter(g => g.date === date);

    // Stage 1: Calculate category-day rankings
    const categoryDayPoints = {};

    players.forEach(player => {
      categoryDayPoints[player.id] = {};
    });

    activeCategories.forEach(category => {
      const categoryGamesOnDate = gamesOnDate.filter(g => g.categoryId === category.id);

      if (categoryGamesOnDate.length === 0) {
        return; // No games in this category on this day
      }

      // Calculate each player's raw points in this category on this day
      const playerRawPoints = players.map(player => {
        let totalRawPoints = 0;

        categoryGamesOnDate.forEach(game => {
          const gameResults = results.filter(r => r.gameId === game.id);
          const playerResult = gameResults.find(r => r.playerId === player.id);
          const effectivePoints = calculateEffectivePoints(playerResult, gameResults);
          totalRawPoints += effectivePoints;
        });

        return {
          playerId: player.id,
          rawPoints: totalRawPoints
        };
      });

      // Rank players by raw points in this category-day
      const sortedByRaw = [...playerRawPoints].sort((a, b) => b.rawPoints - a.rawPoints);

      // First pass: assign ranks
      let currentRank = 1;
      let previousRawPoints = null;
      const rankedCategoryDay = [];

      sortedByRaw.forEach((playerData) => {
        if (previousRawPoints !== null && playerData.rawPoints < previousRawPoints) {
          currentRank = rankedCategoryDay.length + 1;
        }

        previousRawPoints = playerData.rawPoints;

        rankedCategoryDay.push({
          playerId: playerData.playerId,
          rank: currentRank
        });
      });

      // Second pass: calculate averaged placement points for tied players
      rankedCategoryDay.forEach(playerData => {
        const playersWithSameRank = rankedCategoryDay.filter(p => p.rank === playerData.rank);

        let sumOfPositionPoints = 0;
        for (let i = 0; i < playersWithSameRank.length; i++) {
          const positionRank = playerData.rank + i;
          const positionPoints = numPlayers - positionRank + 1;
          sumOfPositionPoints += positionPoints;
        }
        const placementPoints = sumOfPositionPoints / playersWithSameRank.length;

        categoryDayPoints[playerData.playerId][category.id] = placementPoints;
      });
    });

    // Stage 2: Calculate daily rankings
    const dailyScores = players.map(player => {
      // Sum category-day placement points for this player on this day
      const dailyTotal = Object.values(categoryDayPoints[player.id]).reduce((sum, pts) => sum + pts, 0);

      return {
        playerId: player.id,
        dailyScore: dailyTotal,
        categoryPoints: { ...categoryDayPoints[player.id] }
      };
    });

    // Rank players by daily score
    const sortedByDaily = [...dailyScores].sort((a, b) => b.dailyScore - a.dailyScore);

    // First pass: assign ranks
    let currentRank = 1;
    let previousDailyScore = null;
    const rankedDaily = [];

    sortedByDaily.forEach(playerData => {
      if (previousDailyScore !== null && playerData.dailyScore < previousDailyScore) {
        currentRank = rankedDaily.length + 1;
      }

      previousDailyScore = playerData.dailyScore;

      rankedDaily.push({
        playerId: playerData.playerId,
        dailyScore: playerData.dailyScore,
        categoryPoints: playerData.categoryPoints,
        rank: currentRank
      });
    });

    // Second pass: calculate averaged placement points for tied players
    rankedDaily.forEach(playerData => {
      const playersWithSameRank = rankedDaily.filter(p => p.rank === playerData.rank);

      let sumOfPositionPoints = 0;
      for (let i = 0; i < playersWithSameRank.length; i++) {
        const positionRank = playerData.rank + i;
        const positionPoints = numPlayers - positionRank + 1;
        sumOfPositionPoints += positionPoints;
      }
      const dailyPlacementPoints = sumOfPositionPoints / playersWithSameRank.length;

      playerDailyPoints[playerData.playerId][date] = dailyPlacementPoints;
      dailyBreakdown[playerData.playerId][date] = {
        dailyPlacementPoints,
        dailyScore: playerData.dailyScore,
        categoryPoints: playerData.categoryPoints,
        rank: playerData.rank
      };
    });
  });

  // Calculate overall scores (sum of daily placement points)
  return players.map(player => {
    const totalScore = Object.values(playerDailyPoints[player.id]).reduce((sum, pts) => sum + pts, 0);

    return {
      playerId: player.id,
      playerName: player.displayName,
      score: totalScore,
      dailyBreakdown: dailyBreakdown[player.id]
    };
  });
};

// Sort players by score (descending) and add ranks
// Note: This function only assigns ranks, it doesn't modify scores
// Score averaging happens in the individual calculation functions
export const rankPlayers = (playerScores) => {
  const sorted = [...playerScores].sort((a, b) => b.score - a.score);

  let currentRank = 1;
  let previousScore = null;

  return sorted.map((player, index) => {
    if (previousScore !== null && player.score < previousScore) {
      currentRank = index + 1;
    }

    previousScore = player.score;

    return {
      ...player,
      rank: currentRank
    };
  });
};

// Get category leaderboard
export const getCategoryLeaderboard = (players, games, results, categoryId) => {
  // calculateCategoryScores already returns ranked data with placement points
  return calculateCategoryScores(players, games, results, categoryId);
};

// Get overall leaderboard (supports both ranking methods)
export const getOverallLeaderboard = (players, games, results, categories, rankingMethod = 'byCategory') => {
  let scores;

  if (rankingMethod === 'byDay') {
    scores = calculateOverallScoresByDay(players, games, results, categories);
  } else {
    scores = calculateOverallScoresByCategory(players, games, results, categories);
  }

  return rankPlayers(scores);
};

// Calculate average for non-participants in a specific game
export const calculateGameAverage = (gameId, results) => {
  const gameResults = results.filter(r => r.gameId === gameId);
  const participantResults = gameResults.filter(
    r => r.participated && r.pointsEntered !== null
  );

  if (participantResults.length === 0) {
    return null;
  }

  const sum = participantResults.reduce((acc, r) => acc + r.pointsEntered, 0);
  return sum / participantResults.length;
};

// Calculate player's position timeline over the competition
export const calculatePlayerTimeline = (playerId, players, games, results, categories, rankingMethod = 'byCategory') => {
  // Get all played games sorted by date
  const playedGames = games
    .filter(g => g.status === 'played')
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  if (playedGames.length === 0) {
    return [];
  }

  const activeCategories = categories.filter(c => c.status === 'active');
  const timeline = [];

  // Calculate position after each game
  playedGames.forEach((game, index) => {
    // Get all games up to and including this one
    const gamesUpToNow = playedGames.slice(0, index + 1);

    // Calculate overall leaderboard at this point
    const overallLeaderboard = getOverallLeaderboard(
      players,
      gamesUpToNow,
      results,
      activeCategories,
      rankingMethod
    );

    const playerOverallData = overallLeaderboard.find(p => p.playerId === playerId);

    // Calculate category leaderboards at this point
    const categoryData = {};
    activeCategories.forEach(category => {
      const categoryLeaderboard = getCategoryLeaderboard(
        players,
        gamesUpToNow,
        results,
        category.id
      );
      const playerCategoryData = categoryLeaderboard.find(p => p.playerId === playerId);

      if (playerCategoryData && playerCategoryData.score > 0) {
        categoryData[category.id] = {
          rank: playerCategoryData.rank,
          points: Math.round(playerCategoryData.score)
        };
      }
    });

    timeline.push({
      date: game.date,
      gameName: game.name,
      overall: {
        rank: playerOverallData?.rank || null,
        points: Math.round(playerOverallData?.score || 0)
      },
      categories: categoryData
    });
  });

  return timeline;
};
